import React, { useState, useEffect } from "react";
import {
  Shield,
  Lock,
  Key,
  Smartphone,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Copy,
  CheckCheck,
} from "lucide-react";
import { API_BASE_URL } from "../../config/api";

const SecuritySettings = ({ darkMode, user }) => {
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [enablingTwoFactor, setEnablingTwoFactor] = useState(false);
  const [disablingTwoFactor, setDisablingTwoFactor] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState(null);

  // 2FA setup modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // 2FA disable modal state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  useEffect(() => {
    // Check if user has 2FA enabled (you'll need to add this to user object)
    setTwoFactorEnabled(user?.twoFactorEnabled || false);
  }, [user]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to change password");
      }

      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      console.error("Password change error:", error);
      setPasswordError(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    setEnablingTwoFactor(true);
    setTwoFactorError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/enable-2fa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId: user.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to enable 2FA");
      }

      setQrCode(data.qrCode);
      setSecret(data.secret);
      setShowSetupModal(true);
    } catch (error) {
      console.error("Enable 2FA error:", error);
      setTwoFactorError(error.message);
    } finally {
      setEnablingTwoFactor(false);
    }
  };

  const handleVerify2FASetup = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setTwoFactorError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/verify-2fa-setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId: user.id,
          code: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Invalid verification code");
      }

      // Show backup codes
      setBackupCodes(data.backupCodes);
      setShowBackupCodes(true);
      setTwoFactorEnabled(true);
      setVerificationCode("");
    } catch (error) {
      console.error("Verify 2FA error:", error);
      setTwoFactorError(error.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setDisablingTwoFactor(true);
    setTwoFactorError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/disable-2fa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId: user.id,
          password: disablePassword,
          code: disableCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to disable 2FA");
      }

      setTwoFactorEnabled(false);
      setShowDisableModal(false);
      setDisablePassword("");
      setDisableCode("");
    } catch (error) {
      console.error("Disable 2FA error:", error);
      setTwoFactorError(error.message);
    } finally {
      setDisablingTwoFactor(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lyrical-toolkit-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const closeSetupModal = () => {
    setShowSetupModal(false);
    setShowBackupCodes(false);
    setQrCode(null);
    setSecret(null);
    setVerificationCode("");
    setBackupCodes([]);
    setTwoFactorError(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-5 text-forest-100">
          Security Settings
        </h3>
      </div>

      {/* Change Password Section */}
      <div className="p-6 rounded-lg border bg-forest-700 border-forest-600">
        <div className="flex items-center gap-3 mb-5">
          <Lock className="w-6 h-6 text-gold-500" />
          <h4 className="text-lg font-semibold text-forest-100">
            Change Password
          </h4>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1 text-forest-200">
              Current Password
            </label>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem 2.5rem 0.5rem 0.75rem",
                  fontSize: "0.875rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #2a5038",
                  backgroundColor: "#1e3a28",
                  color: "#d0eadb",
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                style={{
                  position: "absolute",
                  right: "0.5rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  padding: "0.25rem",
                  cursor: "pointer",
                  color: "#7ab892",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showCurrentPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-forest-200">
              New Password
            </label>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem 2.5rem 0.5rem 0.75rem",
                  fontSize: "0.875rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #2a5038",
                  backgroundColor: "#1e3a28",
                  color: "#d0eadb",
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{
                  position: "absolute",
                  right: "0.5rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  padding: "0.25rem",
                  cursor: "pointer",
                  color: "#7ab892",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showNewPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-forest-200">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                fontSize: "0.875rem",
                borderRadius: "0.5rem",
                border: "1px solid #2a5038",
                backgroundColor: "#1e3a28",
                color: "#d0eadb",
                outline: "none",
              }}
            />
          </div>

          {passwordError && (
            <div className="p-4 rounded-lg flex items-center gap-2 bg-ember-900/30 border border-ember-700">
              <XCircle className="w-6 h-6 text-ember-400" />
              <span className="text-base text-ember-200">{passwordError}</span>
            </div>
          )}

          {passwordSuccess && (
            <div className="p-4 rounded-lg flex items-center gap-2 bg-leaf-900/30 border border-leaf-700">
              <CheckCircle className="w-6 h-6 text-leaf-400" />
              <span className="text-base text-leaf-200">
                Password changed successfully!
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={changingPassword}
            style={{
              width: "100%",
              padding: "0.625rem 1rem",
              fontSize: "0.875rem",
              borderRadius: "0.5rem",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              border: "none",
              cursor: changingPassword ? "not-allowed" : "pointer",
              backgroundColor: changingPassword ? "#2a5038" : "#c5a34e",
              color: changingPassword ? "#7ab892" : "#0d1a14",
            }}
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Changing Password...
              </>
            ) : (
              "Change Password"
            )}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="p-6 rounded-lg border bg-forest-700 border-forest-600">
        <div className="flex items-center gap-3 mb-5">
          <Smartphone className="w-6 h-6 text-gold-500" />
          <h4 className="text-lg font-semibold text-forest-100">
            Two-Factor Authentication
          </h4>
        </div>

        <p className="text-base mb-5 text-forest-200">
          Add an extra layer of security to your account by requiring a
          verification code in addition to your password.
        </p>

        <div
          className={`p-4 rounded-lg mb-4 ${
            twoFactorEnabled
              ? "bg-leaf-900/20 border border-leaf-700"
              : "bg-forest-800 border border-forest-600"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {twoFactorEnabled ? (
                <CheckCircle className="w-5 h-5 text-leaf-400" />
              ) : (
                <XCircle className="w-5 h-5 text-forest-300" />
              )}
              <span
                className={`font-medium ${
                  twoFactorEnabled ? "text-leaf-400" : "text-forest-300"
                }`}
              >
                {twoFactorEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </div>

        {twoFactorError && (
          <div className="p-4 rounded-lg flex items-center gap-2 mb-5 bg-ember-900/30 border border-ember-700">
            <AlertCircle className="w-6 h-6 text-ember-400" />
            <span className="text-base text-ember-200">{twoFactorError}</span>
          </div>
        )}

        {!twoFactorEnabled ? (
          <button
            onClick={handleEnable2FA}
            disabled={enablingTwoFactor}
            className={`w-full py-4 px-5 text-base rounded-lg font-medium flex items-center justify-center gap-2 ${
              enablingTwoFactor
                ? "bg-forest-600 text-forest-300 cursor-not-allowed"
                : "bg-gold-500 hover:bg-gold-600 text-forest-900"
            }`}
          >
            {enablingTwoFactor ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Shield className="w-6 h-6" />
                Enable Two-Factor Authentication
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() => setShowDisableModal(true)}
            className="w-full py-4 px-5 text-base rounded-lg font-medium flex items-center justify-center gap-2 bg-transparent border-2 border-ember-700 text-ember-400 hover:bg-ember-900/20"
          >
            <XCircle className="w-6 h-6" />
            Disable Two-Factor Authentication
          </button>
        )}
      </div>

      {/* 2FA Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className="w-full p-6 rounded-lg bg-forest-800"
            style={{ maxWidth: "42rem" }}
          >
            {!showBackupCodes ? (
              <>
                <h4 className="text-lg font-semibold mb-4 text-forest-100">
                  Set Up Two-Factor Authentication
                </h4>

                <div className="space-y-4">
                  <p className="text-sm text-forest-200">
                    1. Scan this QR code with your authenticator app (Google
                    Authenticator, Authy, etc.)
                  </p>

                  {qrCode && (
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <img
                        src={qrCode}
                        alt="2FA QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                  )}

                  <p className="text-sm text-forest-200">
                    Or enter this code manually:{" "}
                    <code className="font-mono text-xs">{secret}</code>
                  </p>

                  <form onSubmit={handleVerify2FASetup}>
                    <label className="block text-sm font-medium mb-2 text-forest-200">
                      2. Enter the 6-digit code from your app
                    </label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) =>
                        setVerificationCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-3 py-2 rounded-lg border text-center text-2xl tracking-widest bg-forest-700 border-forest-600 text-forest-100 focus:outline-none focus:ring-2 focus:ring-gold-500"
                    />

                    <div className="flex gap-3 mt-4">
                      <button
                        type="button"
                        onClick={closeSetupModal}
                        disabled={verifying}
                        className="flex-1 py-2 px-4 rounded-lg font-medium bg-forest-700 hover:bg-forest-600 text-forest-100"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={verifying || verificationCode.length !== 6}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                          verifying || verificationCode.length !== 6
                            ? "bg-forest-600 text-forest-300 cursor-not-allowed"
                            : "bg-gold-500 hover:bg-gold-600 text-forest-900"
                        }`}
                      >
                        {verifying ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          "Verify & Enable"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <>
                <h4 className="text-lg font-semibold mb-4 text-forest-100">
                  Save Your Backup Codes
                </h4>

                <div className="p-4 rounded-lg mb-4 bg-gold-900/30 border border-gold-700">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-gold-400" />
                    <p className="text-sm text-gold-200">
                      Save these backup codes in a safe place. You can use them
                      to access your account if you lose your authenticator
                      device.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-lg mb-4 font-mono text-sm bg-forest-700">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="text-forest-100">
                      {code}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mb-4">
                  <button
                    onClick={copyBackupCodes}
                    className="flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-600 text-forest-100"
                  >
                    {copiedCodes ? (
                      <>
                        <CheckCheck className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Codes
                      </>
                    )}
                  </button>
                  <button
                    onClick={downloadBackupCodes}
                    className="flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-600 text-forest-100"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>

                <button
                  onClick={closeSetupModal}
                  className="w-full py-2 px-4 rounded-lg font-medium bg-gold-500 hover:bg-gold-600 text-forest-900"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* 2FA Disable Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className="w-full p-6 rounded-lg bg-forest-800"
            style={{ maxWidth: "42rem" }}
          >
            <h4 className="text-lg font-semibold mb-4 text-forest-100">
              Disable Two-Factor Authentication
            </h4>

            <p className="text-sm mb-4 text-forest-200">
              For security, please enter your password and a verification code
              to disable 2FA.
            </p>

            <form onSubmit={handleDisable2FA} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-forest-200">
                  Password
                </label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-forest-700 border-forest-600 text-forest-100 focus:outline-none focus:ring-2 focus:ring-gold-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-forest-200">
                  6-Digit Code (or backup code)
                </label>
                <input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-forest-700 border-forest-600 text-forest-100 focus:outline-none focus:ring-2 focus:ring-gold-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDisableModal(false);
                    setDisablePassword("");
                    setDisableCode("");
                    setTwoFactorError(null);
                  }}
                  disabled={disablingTwoFactor}
                  className="flex-1 py-2 px-4 rounded-lg font-medium bg-forest-700 hover:bg-forest-600 text-forest-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    disablingTwoFactor || !disablePassword || !disableCode
                  }
                  className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    disablingTwoFactor || !disablePassword || !disableCode
                      ? "bg-forest-600 text-forest-300 cursor-not-allowed"
                      : "bg-ember-600 hover:bg-ember-700 text-forest-100"
                  }`}
                >
                  {disablingTwoFactor ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    "Disable 2FA"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
